/* ==========================================================================
   TOVULI — storefront behaviour
   Vanilla, dependency-free, ~2.5 KB. Every feature degrades to working HTML.
   The FAQ accordion is native <details> and needs no JavaScript at all.
   ========================================================================== */
(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------ */
  function initReveal(root) {
    var items = (root || document).querySelectorAll('.tv-reveal:not([data-tv-seen])');
    if (!items.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.setAttribute('data-tv-seen', '');
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    items.forEach(function (el) {
      el.setAttribute('data-tv-seen', '');
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------
     Header state + sticky purchase bar
     One scroll listener, rAF-throttled, passive.
     ------------------------------------------------------------------ */
  function initScroll() {
    var body = document.body;
    var ticking = false;

    // Horizon's "squeeze" desktop layout (>=990px) scrolls `.page-wrapper`
    // instead of the document itself — window/html/body never move there,
    // so any listener/measurement bound to them silently never fires. Below
    // that breakpoint the document root scrolls natively (so the mobile
    // address bar can hide/show). This mirrors Horizon's own
    // `@theme/scroll-container` helper, which a plain non-module script
    // like this one can't import directly.
    var squeezeQuery = window.matchMedia('(min-width: 990px)');

    function getScrollContainer() {
      if (squeezeQuery.matches) {
        return (
          document.querySelector('.page-wrapper') ||
          document.scrollingElement ||
          document.documentElement
        );
      }
      return document.scrollingElement || document.documentElement;
    }

    function getScrollEventTarget() {
      if (squeezeQuery.matches) {
        return document.querySelector('.page-wrapper') || document;
      }
      return document;
    }

    function update() {
      ticking = false;
      var container = getScrollContainer();
      var y = container.scrollTop;
      body.classList.toggle('tv-scrolled', y > 12);

      // Looked up fresh on every call, not cached at boot: the theme editor
      // (and any lazy section render) can insert this section into the DOM
      // after this listener is already attached. A value cached once at
      // startup would stay null forever and the bar would never appear.
      var sticky = document.querySelector('[data-tv-sticky]');
      if (!sticky) return;

      var trigger = document.getElementById('tv-hero') || document.querySelector('.tv-hero');
      // Show once the hero is behind us, hide again near the footer CTA.
      var past = trigger ? y > trigger.offsetHeight * 0.75 : y > 600;
      var viewport = container === document.documentElement || container === document.scrollingElement
        ? window.innerHeight
        : container.clientHeight;
      var atEnd = y + viewport >= container.scrollHeight - 240;
      var show = past && !atEnd;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      // Keep the hidden bar out of the tab order as well as the a11y tree.
      if (show) sticky.removeAttribute('inert');
      else sticky.setAttribute('inert', '');
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    var boundTarget = null;

    function bindScrollTarget() {
      var target = getScrollEventTarget();
      if (target === boundTarget) return;
      if (boundTarget) boundTarget.removeEventListener('scroll', onScroll);
      target.addEventListener('scroll', onScroll, { passive: true });
      boundTarget = target;
    }

    bindScrollTarget();
    window.addEventListener('resize', onScroll, { passive: true });
    if (squeezeQuery.addEventListener) {
      squeezeQuery.addEventListener('change', function () {
        bindScrollTarget();
        update();
      });
    }
    update();
  }

  /* ------------------------------------------------------------------
     Color picker
     Cross-fades the image, updates the name, price and buy link.
     Falls back to plain links to each variant when JS is unavailable.
     ------------------------------------------------------------------ */
  function initColors(root) {
    var groups = (root || document).querySelectorAll('[data-tv-colors]');

    groups.forEach(function (group) {
      if (group.hasAttribute('data-tv-bound')) return;
      group.setAttribute('data-tv-bound', '');

      var buttons = group.querySelectorAll('[data-tv-swatch]');
      var slides = group.querySelectorAll('[data-tv-slide]');
      var nameEl = group.querySelector('[data-tv-color-name]');
      var glowEl = group.querySelector('[data-tv-glow]');
      var buyEls = group.querySelectorAll('[data-tv-buy]');

      function select(id, focus) {
        buttons.forEach(function (btn) {
          var on = btn.getAttribute('data-tv-swatch') === id;
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on && focus) btn.focus();
        });

        slides.forEach(function (slide) {
          if (slide.getAttribute('data-tv-slide') === id) {
            slide.setAttribute('data-active', '');
            slide.removeAttribute('aria-hidden');
          } else {
            slide.removeAttribute('data-active');
            slide.setAttribute('aria-hidden', 'true');
          }
        });

        var active = group.querySelector('[data-tv-swatch="' + id + '"]');
        if (!active) return;

        if (nameEl) nameEl.textContent = active.getAttribute('data-tv-name') || '';

        var hex = active.getAttribute('data-tv-hex');
        if (glowEl && hex) {
          glowEl.style.background =
            'radial-gradient(circle, ' + hex + '99 0%, transparent 68%)';
        }

        var url = active.getAttribute('data-tv-url');
        if (url) {
          buyEls.forEach(function (el) {
            el.setAttribute('href', url);
          });
        }
      }

      buttons.forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          select(btn.getAttribute('data-tv-swatch'), false);
        });

        // Arrow keys walk the set, matching radio-group expectations.
        btn.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i + 1;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i - 1;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = buttons.length - 1;
          if (next === null) return;
          e.preventDefault();
          var target = buttons[(next + buttons.length) % buttons.length];
          select(target.getAttribute('data-tv-swatch'), true);
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Product page gallery

     The radios do the selling on their own — this only keeps the picture,
     the color name and the price in step with whichever one is checked.
     Prev/next arrow buttons (when present) drive the exact same `sync`
     used by the thumbnails, just computed from the currently active slide
     instead of a click target — so there is exactly one place slide state
     actually changes.
     ------------------------------------------------------------------ */
  function initPdp(root) {
    var panels = (root || document).querySelectorAll('[data-tv-pdp]');

    panels.forEach(function (panel) {
      if (panel.hasAttribute('data-tv-bound')) return;
      panel.setAttribute('data-tv-bound', '');

      var radios = panel.querySelectorAll('[data-tv-radio]');
      var slides = panel.querySelectorAll('[data-tv-slide]');
      var thumbs = panel.querySelectorAll('[data-tv-thumb]');
      var nameEl = panel.querySelector('[data-tv-color-name]');
      var priceEl = panel.querySelector('[data-tv-price]');
      var glowEl = panel.querySelector('[data-tv-glow]');
      var prevBtn = panel.querySelector('[data-tv-prev]');
      var nextBtn = panel.querySelector('[data-tv-next]');

      function sync(id) {
        slides.forEach(function (slide) {
          if (slide.getAttribute('data-tv-slide') === id) {
            slide.setAttribute('data-active', '');
            slide.removeAttribute('aria-hidden');
          } else {
            slide.removeAttribute('data-active');
            slide.setAttribute('aria-hidden', 'true');
          }
        });

        thumbs.forEach(function (t) {
          t.setAttribute('aria-pressed', t.getAttribute('data-tv-thumb') === id ? 'true' : 'false');
        });

        var radio = panel.querySelector('[data-tv-radio="' + id + '"]');
        if (!radio) return;

        if (nameEl) nameEl.textContent = radio.getAttribute('data-tv-name') || '';
        if (priceEl) {
          var p = radio.getAttribute('data-tv-price');
          if (p) priceEl.textContent = p;
        }

        var hex = radio.getAttribute('data-tv-hex');
        if (glowEl && hex) {
          glowEl.style.background =
            'radial-gradient(circle, ' + hex + '88 0%, transparent 70%)';
        }

        // Keep the address bar shareable: ?variant=… so a copied link opens
        // on the color the visitor was actually looking at.
        if (window.history && window.history.replaceState) {
          try {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', id);
            window.history.replaceState({}, '', url);
          } catch (err) {
            /* older browser, non-critical */
          }
        }
      }

      radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) sync(radio.value);
        });
      });

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var id = thumb.getAttribute('data-tv-thumb');
          var radio = panel.querySelector('[data-tv-radio="' + id + '"]');
          if (radio && !radio.disabled) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            sync(id);
          }
        });
      });

      function activeSlideIndex() {
        var idx = 0;
        slides.forEach(function (slide, i) {
          if (slide.hasAttribute('data-active')) idx = i;
        });
        return idx;
      }

      function goToSlide(i) {
        var count = slides.length;
        if (!count) return;
        var next = ((i % count) + count) % count;
        var id = slides[next].getAttribute('data-tv-slide');
        if (!id) return;
        var radio = panel.querySelector('[data-tv-radio="' + id + '"]');
        if (radio && !radio.disabled) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          sync(id);
        }
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goToSlide(activeSlideIndex() - 1);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goToSlide(activeSlideIndex() + 1);
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     Quantity stepper — shared by the product page and the sticky
     quick-buy bar (any `[data-tv-qty]` container). Plain +/- buttons
     around a native number input — the input itself is what Shopify's
     dynamic checkout button reads at submit time, so no extra wiring is
     needed for the purchase to pick up the chosen quantity.
     ------------------------------------------------------------------ */
  function initQty(root) {
    var groups = (root || document).querySelectorAll('[data-tv-qty]:not([data-tv-qty-bound])');

    groups.forEach(function (qty) {
      qty.setAttribute('data-tv-qty-bound', '');

      var qtyInput = qty.querySelector('input[type="number"]');
      var qtyDec = qty.querySelector('[data-tv-qty-dec]');
      var qtyInc = qty.querySelector('[data-tv-qty-inc]');
      if (!qtyInput) return;

      function qtyBounds() {
        var min = parseInt(qtyInput.min, 10);
        var max = parseInt(qtyInput.max, 10);
        return {
          min: isNaN(min) ? 1 : min,
          max: isNaN(max) ? Infinity : max
        };
      }

      function setQty(value) {
        var bounds = qtyBounds();
        var next = Math.min(bounds.max, Math.max(bounds.min, value || bounds.min));
        qtyInput.value = next;
        if (qtyDec) qtyDec.disabled = next <= bounds.min;
        if (qtyInc) qtyInc.disabled = next >= bounds.max;
      }

      if (qtyDec) {
        qtyDec.addEventListener('click', function () {
          setQty((parseInt(qtyInput.value, 10) || 1) - 1);
        });
      }

      if (qtyInc) {
        qtyInc.addEventListener('click', function () {
          setQty((parseInt(qtyInput.value, 10) || 1) + 1);
        });
      }

      qtyInput.addEventListener('change', function () {
        setQty(parseInt(qtyInput.value, 10));
      });

      setQty(parseInt(qtyInput.value, 10) || 1);
    });
  }

  /* ------------------------------------------------------------------
     "More payment options" relocation

     Shopify's dynamic checkout button renders a "More payment options"
     link INSIDE the button's own column (`.tv-pdp__buy`) once its
     accelerated-checkout script has loaded — which happens a beat after
     first paint, hence the MutationObserver rather than a one-off query.
     The design calls for that link to sit centered under BOTH the
     quantity stepper and the buy button, not just under the button's own
     narrower column, so once the node appears it is physically moved
     into the dedicated `[data-tv-more-options]` container that already
     spans the full row. Progressive enhancement: with JS disabled (or
     before the move happens) the link still renders in Shopify's default
     spot inside the button column — never invisible, just not perfectly
     centered yet.

     The button column's height clip (and the hiding of any stray second
     button-like element) now lives entirely in CSS on `.tv-pdp__buy`
     itself (sections/tovuli-product.liquid) — unconditional from first
     paint, not something this script needs to add after the fact — so
     this function only ever has one job: move the link once it exists.
     ------------------------------------------------------------------ */
  function initMoreOptions(root) {
    var containers = (root || document).querySelectorAll(
      '[data-tv-more-options]:not([data-tv-mo-bound])'
    );

    containers.forEach(function (container) {
      container.setAttribute('data-tv-mo-bound', '');

      var row = container.previousElementSibling;
      var buyEl = row ? row.querySelector('.tv-pdp__buy') : null;
      if (!buyEl) return;

      // Shopify can render TWO elements sharing the `__more-options`
      // class: the real link, and (confirmed from the live DOM) a loading
      // skeleton — `<div class="shopify-payment-button__more-options
      // shopify-payment-button__skeleton" ...>` — that never gets removed
      // in this store. `:not(.shopify-payment-button__skeleton)` here
      // makes sure it's always the REAL link that gets relocated, never
      // the skeleton; the skeleton itself is hidden unconditionally by
      // CSS (`.shopify-payment-button__skeleton` in
      // sections/tovuli-product.liquid) wherever it ends up in the DOM.
      function relocate() {
        var node = buyEl.querySelector(
          '.shopify-payment-button__more-options:not(.shopify-payment-button__skeleton)'
        );
        if (node && node.parentNode !== container) {
          container.appendChild(node);
        }
      }

      relocate();

      if ('MutationObserver' in window) {
        var mo = new MutationObserver(relocate);
        mo.observe(buyEl, { childList: true, subtree: true });
      }
    });
  }

  /* ------------------------------------------------------------------
     Thumbnail strip pagination (only wired up when there are more than
     five photos — with five or fewer, everything already fits and these
     buttons don't render). A visible horizontal scrollbar read as a
     stray gray bar under the thumbnails, so scrolling is replaced with a
     small pair of nav arrows that page the strip five photos at a time,
     sliding it with a transform instead. Sizes are computed in pixels
     (not left to CSS percentages) so the math works for any photo count
     and stays correct across resizes.
     ------------------------------------------------------------------ */
  function initThumbsNav(root) {
    var wraps = (root || document).querySelectorAll(
      '[data-tv-thumbs-wrap]:not([data-tv-tn-bound])'
    );

    wraps.forEach(function (wrap) {
      wrap.setAttribute('data-tv-tn-bound', '');

      var track = wrap.querySelector('.tv-pdp__thumbs');
      var prevBtn = wrap.querySelector('[data-tv-thumbs-prev]');
      var nextBtn = wrap.querySelector('[data-tv-thumbs-next]');
      var items = track ? Array.prototype.slice.call(track.children) : [];

      if (!track || items.length <= 5 || (!prevBtn && !nextBtn)) return;

      var perPage = 5;
      var page = 0;

      function layout() {
        var wrapWidth = wrap.clientWidth;
        if (!wrapWidth) return;

        var gapValue = window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap;
        var gap = parseFloat(gapValue) || 0;
        var itemWidth = (wrapWidth - gap * (perPage - 1)) / perPage;

        items.forEach(function (li) {
          // The stylesheet's `.tv-pdp__thumbs li` rule sets both `flex`
          // AND `inline-size` with !important (for the plain 5-photo,
          // no-JS case) — overriding only `flex` here left that
          // percentage-based `inline-size` still winning, which is why
          // every thumbnail rendered squeezed instead of a clean 5-wide
          // window. Setting all three inline, each with 'important',
          // makes this the final word on every photo's width.
          li.style.setProperty('flex', '0 0 ' + itemWidth + 'px', 'important');
          li.style.setProperty('width', itemWidth + 'px', 'important');
          li.style.setProperty('inline-size', itemWidth + 'px', 'important');
        });

        var trackWidth = items.length * itemWidth + (items.length - 1) * gap;
        track.style.width = trackWidth + 'px';

        var maxShift = Math.max(0, trackWidth - wrapWidth);
        var shift = Math.min(page * wrapWidth, maxShift);
        track.style.transform = 'translateX(-' + shift + 'px)';

        if (prevBtn) prevBtn.disabled = shift <= 0;
        if (nextBtn) nextBtn.disabled = shift >= maxShift - 1;
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          page = Math.max(0, page - 1);
          layout();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          page = page + 1;
          layout();
        });
      }

      layout();

      var resizeTimer = null;
      window.addEventListener(
        'resize',
        function () {
          if (resizeTimer) window.clearTimeout(resizeTimer);
          resizeTimer = window.setTimeout(layout, 150);
        },
        { passive: true }
      );
    });
  }

  /* ------------------------------------------------------------------
     Review slider (product page, under the gallery)

     A row of pre-rendered `.tv-pdp__revslide` cards, one visible
     (`data-active`) at a time. Advances automatically every 5 seconds.
     Pauses on hover and while dragging, and resumes after. Left/right
     drag or swipe (mouse or touch, via pointer events) jumps to the
     previous/next review — no dot pager. Pauses while the tab is hidden
     so it never "jumps ahead" while the visitor is on another tab, and
     respects prefers-reduced-motion by not auto-advancing at all (drag
     navigation still works).
     ------------------------------------------------------------------ */
  function initRevSlider(root) {
    var sliders = (root || document).querySelectorAll('[data-tv-revslider]:not([data-tv-rs-bound])');

    sliders.forEach(function (slider) {
      slider.setAttribute('data-tv-rs-bound', '');

      var slides = slider.querySelectorAll('.tv-pdp__revslide');
      var track = slider.querySelector('.tv-pdp__revslider-track');
      if (slides.length < 2 || !track) return;

      var index = 0;
      var timer = null;
      var hovering = false;
      var dragging = false;

      function show(next) {
        index = ((next % slides.length) + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          if (i === index) {
            slide.setAttribute('data-active', '');
            slide.removeAttribute('aria-hidden');
          } else {
            slide.removeAttribute('data-active');
            slide.setAttribute('aria-hidden', 'true');
          }
        });
      }

      function start() {
        if (reduced || hovering || dragging) return;
        stop();
        timer = window.setInterval(function () {
          show(index + 1);
        }, 5000);
      }

      function stop() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }

      // Pause on hover (desktop) — resumes once the pointer leaves.
      track.addEventListener('mouseenter', function () {
        hovering = true;
        stop();
      });
      track.addEventListener('mouseleave', function () {
        hovering = false;
        start();
      });

      // Left/right drag or swipe jumps to the previous/next review. Works
      // for both mouse and touch via pointer events; a horizontal-only
      // threshold (24px) keeps an ordinary tap or vertical page-scroll
      // from being misread as a slide gesture.
      var pointerId = null;
      var startX = 0;
      var startY = 0;
      var moved = false;
      var THRESHOLD = 24;

      track.addEventListener('pointerdown', function (e) {
        if (e.button != null && e.button !== 0) return;
        pointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        moved = false;
        dragging = true;
        stop();
      });

      track.addEventListener('pointermove', function (e) {
        if (pointerId === null || e.pointerId !== pointerId) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        if (!moved && Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          moved = true;
        }
      });

      function endDrag(e) {
        if (pointerId === null || (e.pointerId !== undefined && e.pointerId !== pointerId)) return;
        if (moved) {
          var dx = e.clientX - startX;
          if (dx < 0) show(index + 1);
          else show(index - 1);
        }
        pointerId = null;
        dragging = false;
        start();
      }

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      start();
    });
  }

  /* ------------------------------------------------------------------
     Social share — copy-link button only. The Facebook / X / Pinterest /
     WhatsApp icons are plain share-intent links and need no JS at all.
     ------------------------------------------------------------------ */
  function initShare(root) {
    var buttons = (root || document).querySelectorAll(
      '[data-tv-copy-link]:not([data-tv-copy-bound])'
    );

    buttons.forEach(function (btn) {
      btn.setAttribute('data-tv-copy-bound', '');

      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-url') || window.location.href;

        function announce() {
          btn.classList.add('is-copied');
          var label = btn.getAttribute('aria-label');
          btn.setAttribute('data-tv-prev-label', label || '');
          btn.setAttribute('aria-label', 'Link copied');
          window.setTimeout(function () {
            btn.classList.remove('is-copied');
            var prev = btn.getAttribute('data-tv-prev-label');
            if (prev) btn.setAttribute('aria-label', prev);
          }, 1800);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(announce, announce);
        } else {
          announce();
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Exclusive FAQ accordion — fallback only

     Modern browsers do this themselves from the shared `name` attribute on
     the <details> elements. This runs only where that is unsupported, so
     there is no double handling and no fight with the native behaviour.
     ------------------------------------------------------------------ */
  var supportsExclusive = 'name' in document.createElement('details');

  function initAccordion(root) {
    if (supportsExclusive) return;

    var lists = (root || document).querySelectorAll('.tv-faq__list');

    lists.forEach(function (list) {
      if (list.hasAttribute('data-tv-acc-bound')) return;
      list.setAttribute('data-tv-acc-bound', '');

      list.addEventListener('toggle', function (e) {
        var opened = e.target;
        if (!opened.open || !opened.matches('.tv-acc')) return;
        list.querySelectorAll('.tv-acc[open]').forEach(function (other) {
          if (other !== opened) other.open = false;
        });
      }, true);
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function boot(root) {
    initReveal(root);
    initColors(root);
    initPdp(root);
    initQty(root);
    initMoreOptions(root);
    initThumbsNav(root);
    initRevSlider(root);
    initShare(root);
    initAccordion(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
      initScroll();
    });
  } else {
    boot();
    initScroll();
  }

  // Shopify theme editor: re-bind when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
